import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { CONSTANTS } from "./data";
import { ASSETS } from "./assetManifest";
import { Shield, Skull, Play, RefreshCw, Pause, BookOpen, Home, PlusCircle } from 'lucide-react';
import { Howl } from 'howler';
import { InfoPanel } from "./components/InfoPanel";
import { TroopSelector } from "./components/TroopSelector";
import { MainMenu } from "./components/MainMenu";
import { GameOverModal } from "./components/GameOverModal";
import { PauseModal } from "./components/PauseModal";
import { CodexModal } from "./components/CodexModal";
import { SettingsModal } from "./components/SettingsModal";
import { CreditsModal } from "./components/CreditsModal";
import { motion, useAnimation } from "framer-motion";
import { TutorialGuide } from "./components/TutorialGuide";
import { distance, lerp } from "./utils/math";
import { getRandomEdgePosition, getMergeType, canMergeTroops, getRandomEnemyType } from "./utils/gameHelpers";
import { SplashScreen } from "./components/SplashScreen";

// --- Particle Effect Component ---
interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  initialRadius: number;
  color: string;
  opacity: number;
  age: number;
  maxAge: number;
}

interface ParticleEffectProps {
  isActive: boolean;
  centerX: number;
  centerY: number;
  onComplete: () => void;
}

const ParticleEffect: React.FC<ParticleEffectProps> = ({
  isActive,
  centerX,
  centerY,
  onComplete
}) => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const animationRef = useRef<number>();
  const startTimeRef = useRef<number>();
  const hasParticlesRef = useRef<boolean>(false);

  const createParticles = useCallback(() => {
    const newParticles: Particle[] = [];
    
    for (let i = 0; i < CONSTANTS.PARTICLE_EFFECT.PARTICLE_COUNT; i++) {
      const angle = (Math.PI * 2 * i) / CONSTANTS.PARTICLE_EFFECT.PARTICLE_COUNT + (Math.random() - 0.5) * 0.5;
      const speed = CONSTANTS.PARTICLE_EFFECT.MIN_SPEED + Math.random() * (CONSTANTS.PARTICLE_EFFECT.MAX_SPEED - CONSTANTS.PARTICLE_EFFECT.MIN_SPEED);
      const radius = CONSTANTS.PARTICLE_EFFECT.MIN_RADIUS + Math.random() * (CONSTANTS.PARTICLE_EFFECT.MAX_RADIUS - CONSTANTS.PARTICLE_EFFECT.MIN_RADIUS);
      const maxAge = CONSTANTS.PARTICLE_EFFECT.MIN_LIFESPAN + Math.random() * (CONSTANTS.PARTICLE_EFFECT.MAX_LIFESPAN - CONSTANTS.PARTICLE_EFFECT.MIN_LIFESPAN);
      const colorIndex = Math.floor(Math.random() * CONSTANTS.PARTICLE_EFFECT.COLORS.length);
      
      newParticles.push({
        id: i,
        x: centerX,
        y: centerY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: radius,
        initialRadius: radius,
        color: CONSTANTS.PARTICLE_EFFECT.COLORS[colorIndex],
        opacity: 1,
        age: 0,
        maxAge: maxAge
      });
    }
    
    return newParticles;
  }, [centerX, centerY]);

  const updateParticles = useCallback((deltaTime: number) => {
    setParticles(prevParticles => {
      const updatedParticles = prevParticles.map(particle => {
        const newAge = particle.age + deltaTime;
        const ageRatio = newAge / particle.maxAge;
        
        if (ageRatio >= 1) {
          return null; // Mark for removal
        }
        
        // Apply friction
        const friction = CONSTANTS.PARTICLE_EFFECT.FRICTION;
        const newVx = particle.vx * friction;
        const newVy = particle.vy * friction;
        
        // Update position
        const newX = particle.x + newVx * deltaTime;
        const newY = particle.y + newVy * deltaTime;
        
        // Update size and opacity based on age
        const sizeRatio = 1 - (ageRatio * CONSTANTS.PARTICLE_EFFECT.SIZE_DECAY_RATE);
        const newRadius = particle.initialRadius * Math.max(0, sizeRatio);
        const newOpacity = 1 - ageRatio;
        
        return {
          ...particle,
          x: newX,
          y: newY,
          vx: newVx,
          vy: newVy,
          radius: newRadius,
          opacity: newOpacity,
          age: newAge
        };
      }).filter(particle => particle !== null) as Particle[];
      
      return updatedParticles;
    });
  }, []);

  // Separate effect to handle completion when particles are empty
  useEffect(() => {
    if (hasParticlesRef.current && particles.length === 0) {
      onComplete();
      hasParticlesRef.current = false;
    } else if (particles.length > 0) {
      hasParticlesRef.current = true;
    }
  }, [particles.length, onComplete]);

  const animate = useCallback((currentTime: number) => {
    if (!startTimeRef.current) {
      startTimeRef.current = currentTime;
    }
    
    const deltaTime = (currentTime - startTimeRef.current) / 1000; // Convert to seconds
    startTimeRef.current = currentTime;
    
    updateParticles(deltaTime);
    
    animationRef.current = requestAnimationFrame(animate);
  }, [updateParticles]);

  useEffect(() => {
    if (isActive) {
      setParticles(createParticles());
      startTimeRef.current = undefined;
      hasParticlesRef.current = false;
      animationRef.current = requestAnimationFrame(animate);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      setParticles([]);
      hasParticlesRef.current = false;
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive, createParticles, animate]);

  return (
    <div className="fixed inset-0 pointer-events-none z-40">
      {particles.map(particle => (
        <div
          key={particle.id}
          className="absolute rounded-full"
          style={{
            left: particle.x - particle.radius,
            top: particle.y - particle.radius,
            width: particle.radius * 2,
            height: particle.radius * 2,
            backgroundColor: particle.color,
            opacity: particle.opacity,
            transform: 'translate(-50%, -50%)'
          }}
        />
      ))}
    </div>
  );
};

// --- Sound Setup ---
const menuSelectSound = new Howl({ src: [ASSETS.sfx.menuSelect.path], volume: CONSTANTS.SFX_VOLUME });
const menuClickSound = new Howl({ src: [ASSETS.sfx.menuClick.path], volume: CONSTANTS.SFX_VOLUME });
const placeTroopSound = new Howl({ src: [ASSETS.sfx.placeTroop.path], volume: CONSTANTS.SFX_VOLUME });
const mergeTroopSound = new Howl({ src: [ASSETS.sfx.mergeTroop.path], volume: CONSTANTS.SFX_VOLUME * CONSTANTS.SFX_VOLUME_MULTIPLIERS.mergeTroop });
const rangedAttackSound = new Howl({ src: [ASSETS.sfx.attackRanged.path], volume: CONSTANTS.SFX_VOLUME * CONSTANTS.SFX_VOLUME_MULTIPLIERS.attackRanged });
const meleeAttackSound = new Howl({ src: [ASSETS.sfx.attackMelee.path], volume: CONSTANTS.SFX_VOLUME * CONSTANTS.SFX_VOLUME_MULTIPLIERS.attackMelee });
const enemyHitSound = new Howl({ src: [ASSETS.sfx.enemyHit.path], volume: CONSTANTS.SFX_VOLUME * CONSTANTS.SFX_VOLUME_MULTIPLIERS.enemyHit });
const enemyDieSound = new Howl({ src: [ASSETS.sfx.enemyDie.path], volume: CONSTANTS.SFX_VOLUME * CONSTANTS.SFX_VOLUME_MULTIPLIERS.enemyDie });
const baseHitSound = new Howl({ src: [ASSETS.sfx.baseHit.path], volume: CONSTANTS.SFX_VOLUME * CONSTANTS.SFX_VOLUME_MULTIPLIERS.baseHit });
const gameOverSound = new Howl({ src: [ASSETS.sfx.gameOver.path], volume: CONSTANTS.SFX_VOLUME });
const waveStartSound = new Howl({ src: [ASSETS.sfx.waveStart.path], volume: CONSTANTS.SFX_VOLUME });
const upgradeSound = new Howl({ src: [ASSETS.sfx.upgrade.path], volume: CONSTANTS.SFX_VOLUME });

const mainMenuMusic = new Howl({
  src: [ASSETS.music.mainMenu.path],
  volume: CONSTANTS.MUSIC_VOLUME,
  loop: true,
});
const gameplayMusic = new Howl({
  src: [ASSETS.music.gameplay.path],
  volume: CONSTANTS.MUSIC_VOLUME,
  loop: true,
});

// --- Types ---
type TroopType = keyof typeof CONSTANTS.TROOPS;
type EnemyType = keyof typeof CONSTANTS.ENEMIES;
interface Troop {
  id: number;
  type: TroopType;
  x: number;
  y: number;
  health: number;
  cooldown: number;
  targetEnemyId?: number;
  state: 'idle' | 'moving' | 'attacking';
  originalX?: number;
  originalY?: number;
  creationTime: number;
  facingDirection: 'left' | 'right';
}
interface Enemy {
  id: number;
  type: EnemyType;
  x: number;
  y: number;
  health: number;
  maxHealth: number;
  speed: number;
  spawnTime: number;
  isEngaged: boolean;
  engagedByTroopId?: number;
  facingDirection: 'left' | 'right';
}
interface Projectile {
  id: number;
  x: number;
  y: number;
  targetId: number;
  fromTroopId: number;
  vx: number;
  vy: number;
  damage: number;
}

interface ActiveParticleEffect {
  id: number;
  x: number;
  y: number;
  isActive: boolean;
}

const getBaseTroops = (): Set<TroopType> => {
    const baseTroops = new Set<TroopType>();
    for (const [key, troop] of Object.entries(CONSTANTS.TROOPS)) {
        if (troop.cost > 0) {
            baseTroops.add(key as TroopType);
        }
    }
    return baseTroops;
};

// --- Main App ---
function App() {
  // --- Splash Screen State ---
  const [isSplashVisible, setIsSplashVisible] = useState(true);

  // --- Map Dimensions (calculated once) ---
  const mapWidth = CONSTANTS.BACKGROUND_GRID_COLS * CONSTANTS.BACKGROUND_TILE_SIZE;
  const mapHeight = CONSTANTS.BACKGROUND_GRID_ROWS * CONSTANTS.BACKGROUND_TILE_SIZE;

  // --- Responsive Game Area ---
  const [gameWidth, setGameWidth] = useState(window.innerWidth);
  const [gameHeight, setGameHeight] = useState(window.innerHeight);

  // --- Zoom & Pan ---
  const [zoom, setZoom] = useState(CONSTANTS.ZOOM_INITIAL);
  const [panX, setPanX] = useState(() => window.innerWidth / 2 - (mapWidth / 2) * CONSTANTS.ZOOM_INITIAL);
  const [panY, setPanY] = useState(() => window.innerHeight / 2 - (mapHeight / 2) * CONSTANTS.ZOOM_INITIAL);

  // --- Game State ---
  const [baseHealth, setBaseHealth] = useState(CONSTANTS.BASE_HEALTH);
  const [previousBaseHealth, setPreviousBaseHealth] = useState(CONSTANTS.BASE_HEALTH);
  const [resources, setResources] = useState(CONSTANTS.INITIAL_RESOURCES);
  const [wave, setWave] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [codexOpen, setCodexOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [discoveredTroops, setDiscoveredTroops] = useState<Set<TroopType>>(getBaseTroops);
  const [draggingTroop, setDraggingTroop] = useState<TroopType | null>(null);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const [draggingTroopId, setDraggingTroopId] = useState<number | null>(null);
  const [draggingTroopOriginalPos, setDraggingTroopOriginalPos] = useState<{ x: number; y: number } | null>(null);
  const [hoveredTroopId, setHoveredTroopId] = useState<number | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPosition, setLastPanPosition] = useState<{ x: number; y: number } | null>(null);
  const [maxTroops, setMaxTroops] = useState(CONSTANTS.INITIAL_MAX_TROOPS);

  // --- Volume State ---
  const [musicVolume, setMusicVolume] = useState(() => {
    const savedVolume = localStorage.getItem(CONSTANTS.LOCAL_STORAGE_MUSIC_VOLUME_KEY);
    return savedVolume !== null ? parseFloat(savedVolume) : CONSTANTS.MUSIC_VOLUME;
  });
  const [sfxVolume, setSfxVolume] = useState(() => {
    const savedVolume = localStorage.getItem(CONSTANTS.LOCAL_STORAGE_SFX_VOLUME_KEY);
    return savedVolume !== null ? parseFloat(savedVolume) : CONSTANTS.SFX_VOLUME;
  });

  // --- Tutorial State ---
  const [inTutorial, setInTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);

  // --- High Score ---
  const [highScoreWave, setHighScoreWave] = useState(1);
  useEffect(() => {
    const savedHighScore = localStorage.getItem(CONSTANTS.LOCAL_STORAGE_HIGH_SCORE_KEY);
    if (savedHighScore) {
        setHighScoreWave(parseInt(savedHighScore, 10) || 1);
    }
  }, []);

  // --- Screen Shake State ---
  const [screenShakeActive, setScreenShakeActive] = useState(false);
  const [screenShakeOffset, setScreenShakeOffset] = useState({ x: 0, y: 0 });
  const screenShakeStartTime = useRef<number>(0);
  const screenShakeAnimationId = useRef<number>();

  // --- Dynamic Troop Costs ---
  const [troopCosts, setTroopCosts] = useState<{ [key in TroopType]?: number }>(() => {
    const initialCosts: { [key in TroopType]?: number } = {};
    for (const [key, troop] of Object.entries(CONSTANTS.TROOPS)) {
      if (troop.cost > 0) {
        initialCosts[key as TroopType] = troop.cost;
      }
    }
    return initialCosts;
  });

  // --- Particle Effects State ---
  const [particleEffects, setParticleEffects] = useState<ActiveParticleEffect[]>([]);
  const particleEffectId = useRef(1);

  // --- Entities ---
  const [troops, setTroops] = useState<Troop[]>([]);
  const [enemies, setEnemies] = useState<Enemy[]>([]);
  const [projectiles, setProjectiles] = useState<Projectile[]>([]);

  // --- Background Tiles ---
  const [backgroundTiles, setBackgroundTiles] = useState<string[][]>([]);

  // --- IDs ---
  const troopId = useRef(1);
  const enemyId = useRef(1);
  const projectileId = useRef(1);

  // --- Canvas Ref ---
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // --- Game Loop Timers ---
  const lastTimestamp = useRef<number>(0);
  const waveCycleTimer = useRef<number>(0);
  const spawnTimer = useRef<number>(0);

  // --- Asset Images ---
  const images = useRef<{ [key: string]: HTMLImageElement }>({});

  // --- Animation Controls ---
  const waveAnimationControls = useAnimation();

  // --- Screen Shake Functions ---
  const triggerScreenShake = useCallback(() => {
    if (screenShakeActive) return; // Don't start new shake if one is already active
    
    setScreenShakeActive(true);
    screenShakeStartTime.current = performance.now();
    
    const animateShake = (currentTime: number) => {
      const elapsed = (currentTime - screenShakeStartTime.current) / 1000;
      
      if (elapsed >= CONSTANTS.SCREEN_SHAKE.DURATION) {
        // Shake is complete
        setScreenShakeActive(false);
        setScreenShakeOffset({ x: 0, y: 0 });
        return;
      }
      
      // Calculate shake intensity with decay
      const progress = elapsed / CONSTANTS.SCREEN_SHAKE.DURATION;
      const intensity = CONSTANTS.SCREEN_SHAKE.INTENSITY * (1 - progress);
      
      // Generate random offset
      const angle = Math.random() * Math.PI * 2;
      const magnitude = intensity * Math.random();
      const offsetX = Math.cos(angle) * magnitude;
      const offsetY = Math.sin(angle) * magnitude;
      
      setScreenShakeOffset({ x: offsetX, y: offsetY });
      
      screenShakeAnimationId.current = requestAnimationFrame(animateShake);
    };
    
    screenShakeAnimationId.current = requestAnimationFrame(animateShake);
  }, [screenShakeActive]);

  // --- Monitor Base Health for Screen Shake and Sound ---
  useEffect(() => {
    if (baseHealth < previousBaseHealth && gameStarted && !gameOver) {
      triggerScreenShake();
      baseHitSound.play();
    }
    setPreviousBaseHealth(baseHealth);
  }, [baseHealth, previousBaseHealth, gameStarted, gameOver, triggerScreenShake]);

  // --- Cleanup screen shake animation on unmount ---
  useEffect(() => {
    return () => {
      if (screenShakeAnimationId.current) {
        cancelAnimationFrame(screenShakeAnimationId.current);
      }
    };
  }, []);

  // --- Troop Limit Upgrade Calculation ---
  const troopLimitUpgrades = useMemo(() => maxTroops - CONSTANTS.INITIAL_MAX_TROOPS, [maxTroops]);
  const troopLimitUpgradeCost = useMemo(() => 
      Math.floor(CONSTANTS.TROOP_LIMIT_UPGRADE_INITIAL_COST * (CONSTANTS.TROOP_LIMIT_UPGRADE_COST_INCREASE_RATE ** troopLimitUpgrades)),
      [troopLimitUpgrades]
  );

  const handleUpgradeMaxTroops = useCallback(() => {
      if (resources >= troopLimitUpgradeCost) {
          setResources(r => r - troopLimitUpgradeCost);
          setMaxTroops(m => m + 1);
          upgradeSound.play();
      }
  }, [resources, troopLimitUpgradeCost]);

  // --- Boundary Constraint Functions ---
  const constrainPan = useCallback((newPanX: number, newPanY: number, currentZoom: number) => {
    // Calculate the visible area dimensions
    const visibleWidth = gameWidth / currentZoom;
    const visibleHeight = gameHeight / currentZoom;
    
    // Calculate the maximum pan values to keep the map within bounds
    const maxPanX = Math.max(0, (mapWidth - visibleWidth) * currentZoom);
    const maxPanY = Math.max(0, (mapHeight - visibleHeight) * currentZoom);
    
    // Constrain pan values
    const constrainedPanX = Math.max(-maxPanX, Math.min(0, newPanX));
    const constrainedPanY = Math.max(-maxPanY, Math.min(0, newPanY));
    
    return { x: constrainedPanX, y: constrainedPanY };
  }, [gameWidth, gameHeight, mapWidth, mapHeight]);

  // --- Generate Background Tiles ---
  const generateBackgroundTiles = useCallback(() => {
    const tiles: string[][] = [];
    for (let row = 0; row < CONSTANTS.BACKGROUND_GRID_ROWS; row++) {
      const tileRow: string[] = [];
      for (let col = 0; col < CONSTANTS.BACKGROUND_GRID_COLS; col++) {
        const randomTileType = CONSTANTS.BACKGROUND_TILE_TYPES[
          Math.floor(Math.random() * CONSTANTS.BACKGROUND_TILE_TYPES.length)
        ];
        tileRow.push(randomTileType);
      }
      tiles.push(tileRow);
    }
    return tiles;
  }, []);

  // --- Initialize Background Tiles ---
  useEffect(() => {
    setBackgroundTiles(generateBackgroundTiles());
  }, [generateBackgroundTiles]);

  // --- Coordinate Conversion ---
  const screenToWorld = useCallback((screenX: number, screenY: number) => ({
    x: (screenX - panX) / zoom,
    y: (screenY - panY) / zoom
  }), [zoom, panX, panY]);
  const worldToScreen = useCallback((worldX: number, worldY: number) => ({
    x: worldX * zoom + panX,
    y: worldY * zoom + panY
  }), [zoom, panX, panY]);

  // --- Particle Effect Helpers ---
  const triggerParticleEffect = useCallback((worldX: number, worldY: number) => {
    const screenPos = worldToScreen(worldX, worldY);
    const newEffect: ActiveParticleEffect = {
      id: particleEffectId.current++,
      x: screenPos.x,
      y: screenPos.y,
      isActive: true
    };
    setParticleEffects(prev => [...prev, newEffect]);
  }, [worldToScreen]);

  const handleParticleEffectComplete = useCallback((effectId: number) => {
    setParticleEffects(prev => prev.filter(effect => effect.id !== effectId));
  }, []);

  // --- Responsive Resize ---
  useEffect(() => {
    function handleResize() {
      setGameWidth(window.innerWidth);
      setGameHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  // --- Apply boundary constraints when game dimensions change ---
  useEffect(() => {
    const constrained = constrainPan(panX, panY, zoom);
    if (constrained.x !== panX || constrained.y !== panY) {
      setPanX(constrained.x);
      setPanY(constrained.y);
    }
  }, [gameWidth, gameHeight, panX, panY, zoom, constrainPan]);

  // --- Load Images ---
  useEffect(() => {
    const imgMap: { [key: string]: HTMLImageElement } = {};
    const allAssets = { ...ASSETS.sprites, ...ASSETS.backgrounds };
    Object.entries(allAssets).forEach(([key, asset]) => {
      const img = new window.Image();
      img.src = asset.path;
      imgMap[key] = img;
    });
    images.current = imgMap;
  }, []);

  // --- Mouse Wheel Zoom ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (paused || codexOpen || settingsOpen || creditsOpen) return;
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const worldPos = screenToWorld(mouseX, mouseY);
      const zoomDelta = e.deltaY > 0 ? -CONSTANTS.ZOOM_STEP : CONSTANTS.ZOOM_STEP;
      const newZoom = Math.max(CONSTANTS.ZOOM_MIN, Math.min(CONSTANTS.ZOOM_MAX, zoom + zoomDelta));
      if (newZoom !== zoom) {
        const newPanX = mouseX - worldPos.x * newZoom;
        const newPanY = mouseY - worldPos.y * newZoom;
        const constrained = constrainPan(newPanX, newPanY, newZoom);
        setZoom(newZoom);
        setPanX(constrained.x);
        setPanY(constrained.y);
      }
    };
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [zoom, panX, panY, screenToWorld, paused, codexOpen, settingsOpen, creditsOpen, constrainPan]);

  // --- Music and Sound Management ---
  useEffect(() => {
    mainMenuMusic.volume(musicVolume);
    gameplayMusic.volume(musicVolume);
    localStorage.setItem(CONSTANTS.LOCAL_STORAGE_MUSIC_VOLUME_KEY, String(musicVolume));
  }, [musicVolume]);

  useEffect(() => {
    menuSelectSound.volume(sfxVolume);
    menuClickSound.volume(sfxVolume);
    placeTroopSound.volume(sfxVolume);
    mergeTroopSound.volume(sfxVolume * CONSTANTS.SFX_VOLUME_MULTIPLIERS.mergeTroop);
    rangedAttackSound.volume(sfxVolume * CONSTANTS.SFX_VOLUME_MULTIPLIERS.attackRanged);
    meleeAttackSound.volume(sfxVolume * CONSTANTS.SFX_VOLUME_MULTIPLIERS.attackMelee);
    enemyHitSound.volume(sfxVolume * CONSTANTS.SFX_VOLUME_MULTIPLIERS.enemyHit);
    enemyDieSound.volume(sfxVolume * CONSTANTS.SFX_VOLUME_MULTIPLIERS.enemyDie);
    baseHitSound.volume(sfxVolume * CONSTANTS.SFX_VOLUME_MULTIPLIERS.baseHit);
    gameOverSound.volume(sfxVolume);
    waveStartSound.volume(sfxVolume);
    upgradeSound.volume(sfxVolume);
    localStorage.setItem(CONSTANTS.LOCAL_STORAGE_SFX_VOLUME_KEY, String(sfxVolume));
  }, [sfxVolume]);

  useEffect(() => {
    if (!gameStarted) {
      if (!mainMenuMusic.playing()) {
        gameplayMusic.stop();
        mainMenuMusic.play();
        mainMenuMusic.fade(0, musicVolume, 1000);
      }
    } else {
      if (!gameplayMusic.playing()) {
        mainMenuMusic.fade(musicVolume, 0, 500).once('fade', () => mainMenuMusic.stop());
        gameplayMusic.play();
        gameplayMusic.fade(0, musicVolume, 1000);
      }
    }
  }, [gameStarted, musicVolume]);

  useEffect(() => {
    if (paused) {
      gameplayMusic.fade(musicVolume, CONSTANTS.PAUSED_MUSIC_VOLUME * musicVolume, 500);
    } else if (gameStarted) {
      gameplayMusic.fade(CONSTANTS.PAUSED_MUSIC_VOLUME * musicVolume, musicVolume, 500);
    }
  }, [paused, gameStarted, musicVolume]);

  useEffect(() => {
    if (gameOver) {
      gameplayMusic.fade(gameplayMusic.volume(), 0, 1500).once('fade', () => gameplayMusic.stop());
      gameOverSound.play();
    }
  }, [gameOver]);

  // --- Game Over Detection ---
  useEffect(() => {
    if (baseHealth <= 0 && !gameOver) {
        setGameOver(true);
        if (wave > highScoreWave) {
            setHighScoreWave(wave);
            localStorage.setItem(CONSTANTS.LOCAL_STORAGE_HIGH_SCORE_KEY, wave.toString());
        }
    }
  }, [baseHealth, gameOver, wave, highScoreWave]);

  // --- Wave Animation and Sound ---
  useEffect(() => {
    if (gameStarted) {
        waveAnimationControls.start({
            scale: [1, 1.4, 1],
            transition: { duration: CONSTANTS.WAVE_ANIMATION_DURATION, ease: "easeOut" }
        });
        if (wave > 1 && !inTutorial) {
            waveStartSound.play();
        }
    }
  }, [wave, gameStarted, waveAnimationControls, inTutorial]);

  // --- Game Loop ---
  useEffect(() => {
    if (!gameStarted || gameOver || paused || codexOpen || settingsOpen || creditsOpen) {
      // Still draw the last frame if paused or game over, but don't run logic
      draw();
      return;
    };
    let animationId: number;
    function gameLoop(ts: number) {
      if (gameOver || !gameStarted || paused || codexOpen || settingsOpen || creditsOpen) return;
      if (!lastTimestamp.current) lastTimestamp.current = ts;
      const dt = Math.min((ts - lastTimestamp.current) / 1000, CONSTANTS.MAX_DELTA_TIME);
      lastTimestamp.current = ts;

      // --- Wave Progression ---
      if (!inTutorial) {
        waveCycleTimer.current += dt;
        const totalWaveTime = CONSTANTS.WAVE_INTERVAL + CONSTANTS.WAVE_COOLDOWN;
        if (waveCycleTimer.current >= totalWaveTime) {
          setWave((w) => w + 1);
          waveCycleTimer.current %= totalWaveTime;
        }
      }

      const isSpawningTime = waveCycleTimer.current < CONSTANTS.WAVE_INTERVAL;

      // --- Enemy Spawn ---
      if (isSpawningTime && !inTutorial) {
        spawnTimer.current += dt;
        if (spawnTimer.current >= CONSTANTS.ENEMY_SPAWN_INTERVAL) {
          const numEnemies = Math.min(
            CONSTANTS.ENEMIES_PER_WAVE + wave - 1,
            CONSTANTS.MAX_ENEMIES_PER_WAVE
          );
          for (let i = 0; i < numEnemies; i++) {
            const pos = getRandomEdgePosition(mapWidth, mapHeight);
            const type: EnemyType = getRandomEnemyType(wave) as EnemyType;
            const maxHealth = CONSTANTS.ENEMIES[type].health + (wave - 1) * CONSTANTS.ENEMY_HEALTH_PER_WAVE;
            setEnemies((prev) => [
              ...prev,
              {
                id: enemyId.current++,
                type,
                x: pos.x,
                y: pos.y,
                health: maxHealth,
                maxHealth: maxHealth,
                speed: CONSTANTS.ENEMIES[type].speed + (wave - 1) * CONSTANTS.ENEMY_SPEED_PER_WAVE,
                spawnTime: performance.now() / 1000 + Math.random() * CONSTANTS.ENEMY_SPAWN_TIME_JITTER,
                isEngaged: false,
                facingDirection: 'right',
              },
            ]);
          }
          spawnTimer.current = 0;
        }
      }

      // --- Move Enemies ---
      setEnemies((prevEnemies) =>
        prevEnemies
          .map((enemy) => {
            const dx = mapWidth / 2 - enemy.x;
            const dy = mapHeight / 2 - enemy.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < CONSTANTS.BASE_HIT_DISTANCE) {
              if (!inTutorial) {
                setBaseHealth((h) => Math.max(0, h - CONSTANTS.ENEMIES[enemy.type].damage));
              }
              return null;
            }
            if (enemy.isEngaged) return enemy;
            const speed = enemy.speed * dt * 60;
            const facingDirection = dx < 0 ? 'left' : 'right';
            return {
              ...enemy,
              x: enemy.x + (dx / dist) * speed,
              y: enemy.y + (dy / dist) * speed,
              facingDirection,
            };
          })
          .filter(Boolean) as Enemy[]
      );

      // --- Troop Logic ---
      setTroops((prevTroops) =>
        prevTroops.map((troop) => {
          if (draggingTroopId === troop.id) return { ...troop, cooldown: troop.cooldown };
          const troopData = CONSTANTS.TROOPS[troop.type];
          if (!troopData) throw new Error(`Troop data missing for type: ${troop.type}`);
          let newCooldown = Math.max(0, troop.cooldown - dt);
          let newTroop = { ...troop, cooldown: newCooldown };

          if (troopData.type === "melee" || troopData.type === "hybrid") {
            let currentTarget: Enemy | null = null;
            if (newTroop.targetEnemyId) {
              currentTarget = enemies.find(e => e.id === newTroop.targetEnemyId) || null;
              if (currentTarget && currentTarget.health > 0) {
                const distToCurrentTarget = distance(troop, currentTarget);
                if (distToCurrentTarget > troopData.range) {
                  currentTarget = null;
                  newTroop.targetEnemyId = undefined;
                  setEnemies(prevEnemies =>
                    prevEnemies.map(e =>
                      e.id === troop.targetEnemyId
                        ? { ...e, isEngaged: false, engagedByTroopId: undefined }
                        : e
                    )
                  );
                }
              } else {
                newTroop.targetEnemyId = undefined;
                currentTarget = null;
              }
            }
            if (!currentTarget) {
              let bestTarget: Enemy | null = null;
              let minDist = Infinity;
              for (const enemy of enemies) {
                if (enemy.isEngaged && enemy.engagedByTroopId !== troop.id) continue;
                const d = distance(troop, enemy);
                if (d < troopData.range && d < minDist) {
                  minDist = d;
                  bestTarget = enemy;
                }
              }
              currentTarget = bestTarget;
              if (currentTarget) newTroop.targetEnemyId = currentTarget.id;
            }
            if (currentTarget) {
              const distToTarget = distance(troop, currentTarget);
              if (distToTarget <= CONSTANTS.MELEE_ATTACK_RANGE) {
                newTroop.state = 'attacking';
                setEnemies(prevEnemies =>
                  prevEnemies.map(e =>
                    e.id === currentTarget!.id
                      ? { ...e, isEngaged: true, engagedByTroopId: troop.id }
                      : e
                  )
                );
                if (newCooldown <= 0) {
                  meleeAttackSound.play();
                  enemyHitSound.play();
                  setEnemies(prevEnemies =>
                    prevEnemies.map(e =>
                      e.id === currentTarget!.id
                        ? { ...e, health: e.health - troopData.damage }
                        : e
                    )
                  );
                  newCooldown = troopData.attackCooldown;
                }
              } else {
                newTroop.state = 'moving';
                if (!newTroop.originalX || !newTroop.originalY) {
                  newTroop.originalX = troop.x;
                  newTroop.originalY = troop.y;
                }
                const dx = currentTarget.x - troop.x;
                const dy = currentTarget.y - troop.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                const speed = troopData.speed * dt * 60;
                newTroop.x = troop.x + (dx / dist) * speed;
                newTroop.y = troop.y + (dy / dist) * speed;
                newTroop.facingDirection = dx < 0 ? 'left' : 'right';
              }
            } else {
              if (newTroop.originalX && newTroop.originalY) {
                const dx = newTroop.originalX - troop.x;
                const dy = newTroop.originalY - troop.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist > CONSTANTS.TROOP_RETURN_TO_ORIGINAL_POS_THRESHOLD) {
                  newTroop.state = 'moving';
                  const speed = troopData.speed * dt * 60;
                  newTroop.x = troop.x + (dx / dist) * speed;
                  newTroop.y = troop.y + (dy / dist) * speed;
                  newTroop.facingDirection = dx < 0 ? 'left' : 'right';
                } else {
                  newTroop.state = 'idle';
                  newTroop.x = newTroop.originalX;
                  newTroop.y = newTroop.originalY;
                  newTroop.originalX = undefined;
                  newTroop.originalY = undefined;
                }
              } else {
                newTroop.state = 'idle';
              }
              newTroop.targetEnemyId = undefined;
              setEnemies(prevEnemies =>
                prevEnemies.map(e =>
                  e.engagedByTroopId === troop.id
                    ? { ...e, isEngaged: false, engagedByTroopId: undefined }
                    : e
                )
              );
            }
          }
          
          if (troopData.type === "ranged" || troopData.type === "hybrid") {
            let target: Enemy | null = null;
            let minDist = Infinity;
            for (const enemy of enemies) {
              const d = distance(troop, enemy);
              if (d < troopData.range && d < minDist) {
                minDist = d;
                target = enemy;
              }
            }

            if (target) {
              const dx = target.x - troop.x;
              newTroop.facingDirection = dx < 0 ? 'left' : 'right';

              if (newCooldown <= 0) {
                rangedAttackSound.play();
                const dy = target.y - troop.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                const speed = troopData.projectileSpeed;
                const vx = (dx / dist) * speed;
                const vy = (dy / dist) * speed;
                setProjectiles((prev) => [
                  ...prev,
                  {
                    id: projectileId.current++,
                    x: troop.x,
                    y: troop.y,
                    targetId: target!.id,
                    fromTroopId: troop.id,
                    vx,
                    vy,
                    damage: troopData.damage,
                  },
                ]);
                newCooldown = troopData.attackCooldown;
              }
            }
          }
          return { ...newTroop, cooldown: newCooldown };
        })
      );

      // --- Move Projectiles ---
      setProjectiles((prevProjectiles) =>
        prevProjectiles
          .map((proj) => ({
            ...proj,
            x: proj.x + proj.vx * dt * 60,
            y: proj.y + proj.vy * dt * 60,
          }))
          .filter((proj) =>
            proj.x >= 0 && proj.x <= mapWidth && proj.y >= 0 && proj.y <= mapHeight
          )
      );

      // --- Projectile Hit Detection ---
      setProjectiles((prevProjectiles) => {
        const newProjectiles: Projectile[] = [];
        setEnemies((prevEnemies) => {
          let newEnemies = [...prevEnemies];
          for (const proj of prevProjectiles) {
            const targetIdx = newEnemies.findIndex((e) => e.id === proj.targetId);
            if (targetIdx !== -1) {
              const target = newEnemies[targetIdx];
              if (distance(proj, target) < CONSTANTS.PROJECTILE_HIT_RADIUS) {
                enemyHitSound.play();
                newEnemies[targetIdx] = {
                  ...target,
                  health: target.health - proj.damage,
                };
                continue;
              }
            }
            newProjectiles.push(proj);
          }
          const aliveEnemies = newEnemies.filter((e) => {
            if (e.health <= 0) {
              enemyDieSound.play();
              // Trigger particle effect at enemy position
              triggerParticleEffect(e.x, e.y);
              if (!inTutorial) {
                setResources((r) => r + CONSTANTS.RESOURCE_PER_KILL);
              }
              setTroops(prevTroops =>
                prevTroops.map(t =>
                  t.targetEnemyId === e.id
                    ? { ...t, targetEnemyId: undefined, state: 'idle' as const }
                    : t
                )
              );
              return false;
            }
            return true;
          });
          return aliveEnemies;
        });
        return newProjectiles;
      });

      draw(ts);
      animationId = requestAnimationFrame(gameLoop);
    }
    animationId = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(animationId);
  }, [gameStarted, gameOver, baseHealth, enemies, troops, projectiles, wave, mapWidth, mapHeight, draggingTroopId, zoom, panX, panY, paused, codexOpen, settingsOpen, creditsOpen, triggerParticleEffect, inTutorial]);

  // --- Drag and Drop for New Troop ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !draggingTroop || paused || codexOpen || settingsOpen || creditsOpen) return;
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const worldPos = screenToWorld(screenX, screenY);
      setDragPosition(worldPos);
    };
    const handleMouseUp = () => {
      if (!draggingTroop || !dragPosition) return;
      const currentCost = troopCosts[draggingTroop] || CONSTANTS.TROOPS[draggingTroop].cost;
      const isPlaceable = distance(dragPosition, { x: mapWidth / 2, y: mapHeight / 2 }) <= CONSTANTS.TROOP_PLACEMENT_RADIUS;
      if (isPlaceable && resources >= currentCost && troops.length < maxTroops) {
        placeTroopSound.play();
        setResources((r) => r - currentCost);
        setTroops((prev) => [
          ...prev,
          {
            id: troopId.current++,
            type: draggingTroop,
            x: dragPosition.x,
            y: dragPosition.y,
            health: CONSTANTS.TROOPS[draggingTroop].health,
            cooldown: 0,
            state: 'idle',
            creationTime: performance.now() / 1000,
            facingDirection: 'right',
          },
        ]);
        setDiscoveredTroops(prev => {
            const newSet = new Set(prev);
            newSet.add(draggingTroop);
            return newSet;
        });
        // Increase the cost of this troop type
        if (!inTutorial) {
          setTroopCosts(prev => ({
            ...prev,
            [draggingTroop]: Math.floor(currentCost * CONSTANTS.TROOP_COST_INCREASE_RATE)
          }));
        }

        // Tutorial advancement
        if (inTutorial) {
          if (tutorialStep === 1 && draggingTroop === 'melee') {
            setTutorialStep(2);
          } else if (tutorialStep === 3 && draggingTroop === 'melee') {
            setTutorialStep(4);
          }
        }
      }
      setDraggingTroop(null);
      setDragPosition(null);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingTroop, dragPosition, mapWidth, mapHeight, resources, screenToWorld, paused, codexOpen, settingsOpen, creditsOpen, troops, maxTroops, troopCosts, inTutorial, tutorialStep]);

  // --- Drag and Drop for Moving Existing Troop ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || draggingTroopId === null || paused || codexOpen || settingsOpen || creditsOpen) return;
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const worldPos = screenToWorld(screenX, screenY);
      setDragPosition(worldPos);
    };
    const handleMouseUp = () => {
      if (draggingTroopId === null || !dragPosition) return;
      const isPlaceable = distance(dragPosition, { x: mapWidth / 2, y: mapHeight / 2 }) <= CONSTANTS.TROOP_PLACEMENT_RADIUS;
      
      // Check for troop merging
      const draggedTroop = troops.find(t => t.id === draggingTroopId);
      if (draggedTroop && isPlaceable) {
        let mergeTarget: Troop | null = null;
        for (const troop of troops) {
          if (troop.id !== draggingTroopId && distance(dragPosition, troop) <= CONSTANTS.TROOP_MERGE_DISTANCE) {
            if (canMergeTroops(draggedTroop.type, troop.type)) {
              mergeTarget = troop;
              break;
            }
          }
        }
        
        if (mergeTarget) {
          // Perform merge
          const mergedType = getMergeType(draggedTroop.type, mergeTarget.type);
          if (mergedType) {
            mergeTroopSound.play();
            const mergedTroopData = CONSTANTS.TROOPS[mergedType as TroopType];
            setTroops((prevTroops) => {
              const newTroops = prevTroops.filter(t => t.id !== draggingTroopId && t.id !== mergeTarget!.id);
              return [
                ...newTroops,
                {
                  id: troopId.current++,
                  type: mergedType as TroopType,
                  x: mergeTarget!.x,
                  y: mergeTarget!.y,
                  health: mergedTroopData.health,
                  cooldown: 0,
                  state: 'idle' as const,
                  creationTime: performance.now() / 1000,
                  facingDirection: 'right',
                }
              ];
            });
            setDiscoveredTroops(prev => {
                const newSet = new Set(prev);
                newSet.add(mergedType as TroopType);
                return newSet;
            });

            // Tutorial advancement
            if (inTutorial && tutorialStep === 4 && mergedType === 'champion_warrior') {
              setTutorialStep(5);
            }
          }
        } else {
          // Normal move
          setTroops((prevTroops) => {
            return prevTroops.map((troop) => {
              if (troop.id === draggingTroopId) {
                return {
                  ...troop,
                  x: dragPosition.x,
                  y: dragPosition.y,
                  originalX: dragPosition.x,
                  originalY: dragPosition.y,
                  state: 'idle' as const
                };
              }
              return troop;
            });
          });
        }
      } else if (draggingTroopOriginalPos) {
        // Return to original position if not placeable
        setTroops((prevTroops) => {
          return prevTroops.map((troop) => {
            if (troop.id === draggingTroopId) {
              return { ...troop, x: draggingTroopOriginalPos.x, y: draggingTroopOriginalPos.y };
            }
            return troop;
          });
        });
      }
      
      setDraggingTroopId(null);
      setDraggingTroopOriginalPos(null);
      setDragPosition(null);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingTroopId, dragPosition, mapWidth, mapHeight, draggingTroopOriginalPos, screenToWorld, paused, codexOpen, settingsOpen, creditsOpen, troops, inTutorial, tutorialStep]);

  // --- Mouse Panning ---
  useEffect(() => {
    if (!isPanning) return;

    const handleMouseMove = (e: MouseEvent) => {
        if (!lastPanPosition) return;
        const dx = e.clientX - lastPanPosition.x;
        const dy = e.clientY - lastPanPosition.y;
        const newPanX = panX + dx;
        const newPanY = panY + dy;
        const constrained = constrainPan(newPanX, newPanY, zoom);
        setPanX(constrained.x);
        setPanY(constrained.y);
        setLastPanPosition({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
        setIsPanning(false);
        setLastPanPosition(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isPanning, lastPanPosition, panX, panY, zoom, constrainPan]);

  // --- Troop Hover Detection ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    function handleMouseMove(e: MouseEvent) {
      if (paused || codexOpen || settingsOpen || creditsOpen) return;
      if (draggingTroop || draggingTroopId !== null || isPanning) {
        if (hoveredTroopId !== null) setHoveredTroopId(null);
        return;
      }
      const rect = canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const worldPos = screenToWorld(screenX, screenY);
      let found = false;
      for (const troop of troops) {
        if (distance(worldPos, troop) < CONSTANTS.TROOP_HOVER_DISTANCE) {
          if (hoveredTroopId !== troop.id) setHoveredTroopId(troop.id);
          found = true;
          break;
        }
      }
      if (!found && hoveredTroopId !== null) setHoveredTroopId(null);
    }
    function handleMouseLeave() {
      if (hoveredTroopId !== null) setHoveredTroopId(null);
    }
    canvas.addEventListener("mousemove", handleMouseMove);
    canvas.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      canvas.removeEventListener("mousemove", handleMouseMove);
      canvas.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [troops, draggingTroop, draggingTroopId, mapWidth, mapHeight, hoveredTroopId, screenToWorld, paused, codexOpen, settingsOpen, creditsOpen, isPanning]);

  // --- Draw Function ---
  function draw(ts?: number) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, gameWidth, gameHeight);

    if (!gameStarted) {
        // Don't draw the game world if the menu is up
        return;
    }

    ctx.save();
    ctx.translate(panX, panY);
    ctx.scale(zoom, zoom);

    // Draw background tiles
    for (let row = 0; row < backgroundTiles.length; row++) {
      for (let col = 0; col < backgroundTiles[row].length; col++) {
        const tileType = backgroundTiles[row][col] as keyof typeof CONSTANTS.DRAWING.BACKGROUND_TILE_FALLBACK_COLORS;
        const tileImg = images.current[tileType];
        const x = col * CONSTANTS.BACKGROUND_TILE_SIZE;
        const y = row * CONSTANTS.BACKGROUND_TILE_SIZE;
        const overlap = CONSTANTS.BACKGROUND_TILE_OVERLAP || 1;
        
        if (tileImg && tileImg.complete) {
          ctx.drawImage(
            tileImg,
            x - overlap,
            y - overlap,
            CONSTANTS.BACKGROUND_TILE_SIZE + overlap * 2,
            CONSTANTS.BACKGROUND_TILE_SIZE + overlap * 2
          );
        } else {
          // Fallback colors for each tile type
          ctx.fillStyle = CONSTANTS.DRAWING.BACKGROUND_TILE_FALLBACK_COLORS[tileType] || "#228B22";
          ctx.fillRect(
            x - overlap, 
            y - overlap, 
            CONSTANTS.BACKGROUND_TILE_SIZE + overlap * 2, 
            CONSTANTS.BACKGROUND_TILE_SIZE + overlap * 2
          );
        }
      }
    }

    // Placement radius
    if ((draggingTroop && dragPosition) || (draggingTroopId !== null && dragPosition) || (inTutorial && (tutorialStep === 1 || tutorialStep === 3))) {
      ctx.beginPath();
      ctx.arc(mapWidth / 2, mapHeight / 2, CONSTANTS.TROOP_PLACEMENT_RADIUS, 0, 2 * Math.PI);
      let isPlaceable = false;
      if (dragPosition) {
        isPlaceable = distance(dragPosition, { x: mapWidth / 2, y: mapHeight / 2 }) <= CONSTANTS.TROOP_PLACEMENT_RADIUS;
      }
      ctx.strokeStyle = isPlaceable ? CONSTANTS.DRAWING.PLACEMENT_RADIUS_VALID_COLOR : CONSTANTS.DRAWING.PLACEMENT_RADIUS_INVALID_COLOR;
      ctx.lineWidth = CONSTANTS.DRAWING.PLACEMENT_RADIUS_LINE_WIDTH / zoom;
      ctx.stroke();
    }

    // Troop range when dragging
    if (dragPosition) {
      let troopType: TroopType | null = null;
      if (draggingTroop) troopType = draggingTroop;
      else if (draggingTroopId !== null) {
        const troop = troops.find(t => t.id === draggingTroopId);
        if (troop) troopType = troop.type;
      }
      if (troopType) {
        const troopData = CONSTANTS.TROOPS[troopType];
        if (troopData && typeof troopData.range === "number") {
          const style = CONSTANTS.DRAWING.DRAGGING_TROOP_RANGE_STYLE;
          ctx.save();
          ctx.beginPath();
          ctx.arc(dragPosition.x, dragPosition.y, troopData.range, 0, 2 * Math.PI);
          ctx.strokeStyle = style.strokeStyle;
          ctx.lineWidth = style.lineWidth / zoom;
          ctx.setLineDash([style.lineDash[0] / zoom, style.lineDash[1] / zoom]);
          ctx.shadowColor = style.shadowColor;
          ctx.shadowBlur = style.shadowBlur / zoom;
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.restore();
        }
      }
    }

    // Merge indicator when dragging troop over another
    if (draggingTroopId !== null && dragPosition) {
      const draggedTroop = troops.find(t => t.id === draggingTroopId);
      if (draggedTroop) {
        for (const troop of troops) {
          if (troop.id !== draggingTroopId && distance(dragPosition, troop) <= CONSTANTS.TROOP_MERGE_DISTANCE) {
            if (canMergeTroops(draggedTroop.type, troop.type)) {
              const style = CONSTANTS.DRAWING.MERGE_INDICATOR_STYLE;
              ctx.save();
              ctx.beginPath();
              ctx.arc(troop.x, troop.y, CONSTANTS.TROOP_MERGE_DISTANCE, 0, 2 * Math.PI);
              ctx.strokeStyle = style.strokeStyle;
              ctx.lineWidth = style.lineWidth / zoom;
              ctx.setLineDash([style.lineDash[0] / zoom, style.lineDash[1] / zoom]);
              ctx.stroke();
              ctx.setLineDash([]);
              ctx.restore();
              break;
            }
          }
        }
      }
    }

    // Draw base
    const baseImg = images.current["base"];
    const baseSize = CONSTANTS.DRAWING.BASE_SIZE;
    if (baseImg && baseImg.complete) {
      ctx.drawImage(
        baseImg,
        mapWidth / 2 - baseSize / 2,
        mapHeight / 2 - baseSize / 2,
        baseSize,
        baseSize
      );
    } else {
      ctx.beginPath();
      ctx.arc(mapWidth / 2, mapHeight / 2, baseSize / 2, 0, 2 * Math.PI);
      ctx.fillStyle = CONSTANTS.DRAWING.BASE_FALLBACK_COLOR;
      ctx.fill();
    }

    // Troop attack range if hovered
    if (hoveredTroopId !== null) {
      const hoveredTroop = troops.find(t => t.id === hoveredTroopId);
      if (hoveredTroop) {
        const troopData = CONSTANTS.TROOPS[hoveredTroop.type];
        if (troopData && typeof troopData.range === 'number') {
          const style = CONSTANTS.DRAWING.DRAGGING_TROOP_RANGE_STYLE;
          ctx.save();
          ctx.beginPath();
          ctx.arc(hoveredTroop.x, hoveredTroop.y, troopData.range, 0, 2 * Math.PI);
          ctx.strokeStyle = style.strokeStyle;
          ctx.lineWidth = style.lineWidth / zoom;
          ctx.setLineDash([style.lineDash[0] / zoom, style.lineDash[1] / zoom]);
          ctx.shadowColor = style.shadowColor;
          ctx.shadowBlur = style.shadowBlur / zoom;
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.restore();
        }
      }
    }

    // Draw troop shadows
    for (const troop of troops) {
      if (draggingTroopId === troop.id && dragPosition) continue;

      ctx.save();
      ctx.translate(troop.x, troop.y + CONSTANTS.SHADOW_OFFSET_Y);
      ctx.scale(CONSTANTS.SHADOW_SCALE_X, CONSTANTS.SHADOW_SCALE_Y);
      ctx.globalAlpha = CONSTANTS.SHADOW_OPACITY;
      ctx.fillStyle = CONSTANTS.SHADOW_COLOR;
      ctx.beginPath();
      ctx.arc(0, 0, CONSTANTS.DRAWING.TROOP_SHADOW_RADIUS, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();
    }

    // Draw troops
    for (const troop of troops) {
      if (draggingTroopId === troop.id && dragPosition) continue;

      ctx.save();
      ctx.translate(troop.x, troop.y);

      if (troop.facingDirection === 'left') {
        ctx.scale(-1, 1);
      }

      // --- ANIMATION FIX: Animate all moving troops with speed > 0 ---
      const troopData = CONSTANTS.TROOPS[troop.type];
      if (!troopData) throw new Error(`Troop data missing for type: ${troop.type}`);
      if (troopData.speed > 0 && troop.state === 'moving') {
        const now = (typeof ts === "number" ? ts : performance.now()) / 1000;
        const elapsed = now - troop.creationTime;
        const duration = CONSTANTS.TROOP_ANIMATION_DURATION;
        const phase = ((elapsed % duration) / duration);
        let scaleX: number, scaleY: number;
        if (phase < 0.5) {
          const t = phase / 0.5;
          scaleX = lerp(CONSTANTS.TROOP_SQUASH_SCALE_X, CONSTANTS.TROOP_STRETCH_SCALE_X, t);
          scaleY = lerp(CONSTANTS.TROOP_SQUASH_SCALE_Y, CONSTANTS.TROOP_STRETCH_SCALE_Y, t);
        } else {
          const t = (phase - 0.5) / 0.5;
          scaleX = lerp(CONSTANTS.TROOP_STRETCH_SCALE_X, CONSTANTS.TROOP_SQUASH_SCALE_X, t);
          scaleY = lerp(CONSTANTS.TROOP_STRETCH_SCALE_Y, CONSTANTS.TROOP_SQUASH_SCALE_Y, t);
        }
        ctx.scale(scaleX, scaleY);
      }

      const img = images.current[troop.type];
      const troopSize = CONSTANTS.DRAWING.TROOP_SIZE;
      if (img && img.complete) {
        ctx.drawImage(
          img,
          -troopSize / 2,
          -troopSize / 2,
          troopSize,
          troopSize
        );
      } else {
        ctx.beginPath();
        ctx.arc(0, 0, troopSize / 2, 0, 2 * Math.PI);
        ctx.fillStyle = CONSTANTS.DRAWING.TROOP_FALLBACK_COLOR;
        ctx.fill();
      }
      ctx.restore();

      if (hoveredTroopId === troop.id) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(troop.x, troop.y, CONSTANTS.DRAWING.TROOP_HOVER_RADIUS, 0, 2 * Math.PI);
        ctx.strokeStyle = CONSTANTS.TROOP_HOVER_OUTLINE_COLOR;
        ctx.lineWidth = CONSTANTS.TROOP_HOVER_OUTLINE_WIDTH / zoom;
        ctx.shadowColor = CONSTANTS.TROOP_HOVER_OUTLINE_COLOR;
        ctx.shadowBlur = CONSTANTS.DRAWING.TROOP_HOVER_SHADOW_BLUR / zoom;
        ctx.stroke();
        ctx.restore();
      }
    }

    // Draw enemy shadows
    for (const enemy of enemies) {
      ctx.save();
      ctx.translate(enemy.x, enemy.y + CONSTANTS.SHADOW_OFFSET_Y);
      ctx.scale(CONSTANTS.SHADOW_SCALE_X, CONSTANTS.SHADOW_SCALE_Y);
      ctx.globalAlpha = CONSTANTS.SHADOW_OPACITY;
      ctx.fillStyle = CONSTANTS.SHADOW_COLOR;
      ctx.beginPath();
      
      const shadowRadius = CONSTANTS.ENEMIES[enemy.type].shadowRadius;
      ctx.arc(0, 0, shadowRadius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();
    }

    // Draw enemies with squash & stretch animation
    for (const enemy of enemies) {
      const enemyData = CONSTANTS.ENEMIES[enemy.type];
      ctx.save();
      ctx.translate(enemy.x, enemy.y);

      // Draw health bar (before sprite transforms)
      if (enemy.health < enemy.maxHealth) {
        const healthPercentage = Math.max(0, enemy.health / enemy.maxHealth);
        const barWidth = enemyData.healthBarWidth;
        const barHeight = CONSTANTS.ENEMY_HEALTH_BAR_HEIGHT;
        const yOffset = CONSTANTS.ENEMY_HEALTH_BAR_Y_OFFSET;
        
        // Background
        ctx.fillStyle = CONSTANTS.ENEMY_HEALTH_BAR_BG_COLOR;
        ctx.fillRect(-barWidth / 2, yOffset, barWidth, barHeight);
        
        // Foreground
        ctx.fillStyle = CONSTANTS.ENEMY_HEALTH_BAR_FG_COLOR;
        ctx.fillRect(-barWidth / 2, yOffset, barWidth * healthPercentage, barHeight);
      }

      if (enemy.facingDirection === 'left') {
        ctx.scale(-1, 1);
      }

      if (!enemy.isEngaged) {
        const now = (typeof ts === "number" ? ts : performance.now()) / 1000;
        const elapsed = now - (enemy.spawnTime || 0);

        // --- Animation duration proportional to speed ---
        // duration = baseDuration * (baseSpeed / enemy.speed)
        const baseDuration = CONSTANTS.ENEMY_ANIMATION_BASE_DURATION;
        const baseSpeed = CONSTANTS.ENEMY_ANIMATION_BASE_SPEED;
        // Avoid division by zero
        const duration = baseDuration * (baseSpeed / Math.max(0.01, enemy.speed));

        const phase = ((elapsed % duration) / duration);
        let scaleX: number, scaleY: number;
        if (phase < 0.5) {
          const t = phase / 0.5;
          scaleX = lerp(CONSTANTS.ENEMY_SQUASH_SCALE_X, CONSTANTS.ENEMY_STRETCH_SCALE_X, t);
          scaleY = lerp(CONSTANTS.ENEMY_SQUASH_SCALE_Y, CONSTANTS.ENEMY_STRETCH_SCALE_Y, t);
        } else {
          const t = (phase - 0.5) / 0.5;
          scaleX = lerp(CONSTANTS.ENEMY_STRETCH_SCALE_X, CONSTANTS.ENEMY_SQUASH_SCALE_X, t);
          scaleY = lerp(CONSTANTS.ENEMY_STRETCH_SCALE_Y, CONSTANTS.ENEMY_SQUASH_SCALE_Y, t);
        }
        ctx.scale(scaleX, scaleY);
      }
      let imgKey = "enemy";
      if (enemy.type === "fast") imgKey = "enemy_fast";
      else if (enemy.type === "tank") imgKey = "enemy_tank";
      else if (enemy.type === "swarm") imgKey = "enemy_swarm";
      else if (enemy.type === "brute") imgKey = "enemy_brute";
      const img = images.current[imgKey];
      const enemySize = enemyData.size;
      if (img && img.complete) {
        ctx.drawImage(img, -enemySize / 2, -enemySize / 2, enemySize, enemySize);
      } else {
        ctx.beginPath();
        ctx.arc(0, 0, enemyData.fallback.radius, 0, 2 * Math.PI);
        ctx.fillStyle = enemyData.fallback.color;
        ctx.fill();
      }
      ctx.restore();
    }

    // Draw projectiles
    for (const proj of projectiles) {
      const img = images.current["projectile"];
      const projSize = CONSTANTS.DRAWING.PROJECTILE_SIZE;
      if (img && img.complete) {
        ctx.drawImage(img, proj.x - projSize / 2, proj.y - projSize / 2, projSize, projSize);
      } else {
        ctx.beginPath();
        ctx.arc(proj.x, proj.y, projSize / 2, 0, 2 * Math.PI);
        ctx.fillStyle = CONSTANTS.DRAWING.PROJECTILE_FALLBACK_COLOR;
        ctx.fill();
      }
    }

    // Dragging troop ghost (new troop)
    if (draggingTroop && dragPosition) {
      // Draw shadow for dragging troop
      ctx.save();
      ctx.translate(dragPosition.x, dragPosition.y + CONSTANTS.SHADOW_OFFSET_Y);
      ctx.scale(CONSTANTS.SHADOW_SCALE_X, CONSTANTS.SHADOW_SCALE_Y);
      ctx.globalAlpha = CONSTANTS.SHADOW_OPACITY * CONSTANTS.DRAWING.DRAGGING_GHOST_OPACITY;
      ctx.fillStyle = CONSTANTS.SHADOW_COLOR;
      ctx.beginPath();
      ctx.arc(0, 0, CONSTANTS.DRAWING.TROOP_SHADOW_RADIUS, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();

      const img = images.current[draggingTroop];
      ctx.globalAlpha = CONSTANTS.DRAWING.DRAGGING_GHOST_OPACITY;
      const troopSize = CONSTANTS.DRAWING.TROOP_SIZE;
      if (img && img.complete) {
        ctx.drawImage(img, dragPosition.x - troopSize / 2, dragPosition.y - troopSize / 2, troopSize, troopSize);
      } else {
        ctx.beginPath();
        ctx.arc(dragPosition.x, dragPosition.y, troopSize / 2, 0, 2 * Math.PI);
        ctx.fillStyle = CONSTANTS.DRAWING.TROOP_FALLBACK_COLOR;
        ctx.fill();
      }
      ctx.globalAlpha = 1.0;
    }
    // Dragging troop ghost (move existing)
    if (draggingTroopId !== null && dragPosition) {
      const troop = troops.find(t => t.id === draggingTroopId);
      if (troop) {
        // Draw shadow for dragging troop
        ctx.save();
        ctx.translate(dragPosition.x, dragPosition.y + CONSTANTS.SHADOW_OFFSET_Y);
        ctx.scale(CONSTANTS.SHADOW_SCALE_X, CONSTANTS.SHADOW_SCALE_Y);
        ctx.globalAlpha = CONSTANTS.SHADOW_OPACITY * CONSTANTS.DRAWING.DRAGGING_GHOST_OPACITY;
        ctx.fillStyle = CONSTANTS.SHADOW_COLOR;
        ctx.beginPath();
        ctx.arc(0, 0, CONSTANTS.DRAWING.TROOP_SHADOW_RADIUS, 0, 2 * Math.PI);
        ctx.fill();
        ctx.restore();

        const img = images.current[troop.type];
        ctx.globalAlpha = CONSTANTS.DRAWING.DRAGGING_GHOST_OPACITY;
        const troopSize = CONSTANTS.DRAWING.TROOP_SIZE;
        if (img && img.complete) {
          ctx.drawImage(img, dragPosition.x - troopSize / 2, dragPosition.y - troopSize / 2, troopSize, troopSize);
        } else {
          ctx.beginPath();
          ctx.arc(dragPosition.x, dragPosition.y, troopSize / 2, 0, 2 * Math.PI);
          ctx.fillStyle = CONSTANTS.DRAWING.TROOP_FALLBACK_COLOR;
          ctx.fill();
        }
        ctx.globalAlpha = 1.0;
      }
    }
    ctx.restore();
  }

  // --- Troop Selection Mouse Down ---
  const handleTroopMouseDown = (e: React.MouseEvent, troopType: TroopType) => {
    if (gameOver || !gameStarted || paused || codexOpen || settingsOpen || creditsOpen) return;
    if (draggingTroopId !== null) return;
    if (troops.length >= maxTroops) return;
    const currentCost = troopCosts[troopType] || CONSTANTS.TROOPS[troopType].cost;
    if (resources < currentCost) return;
    e.preventDefault();
    setDraggingTroop(troopType);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    const worldPos = screenToWorld(screenX, screenY);
    setDragPosition(worldPos);
  };

  // --- Canvas Mouse Down (move existing troop or pan) ---
  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    if (e.button !== 0) return; // Only handle left clicks
    if (gameOver || !gameStarted || paused || codexOpen || settingsOpen || creditsOpen) return;
    if (draggingTroop || draggingTroopId !== null) return; // Prevent starting new action while another is in progress

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    const worldPos = screenToWorld(screenX, screenY);

    // Prioritize dragging a troop
    for (const troop of troops) {
      if (distance(worldPos, troop) < CONSTANTS.TROOP_CLICK_DISTANCE) {
        setDraggingTroopId(troop.id);
        setDraggingTroopOriginalPos({ x: troop.x, y: troop.y });
        setDragPosition(worldPos);
        e.preventDefault(); // Prevent default browser actions like text selection
        return; // Exit after finding a troop
      }
    }

    // If no troop was clicked, start panning
    setIsPanning(true);
    setLastPanPosition({ x: e.clientX, y: e.clientY });
    e.preventDefault();
  };

  // --- Game Start/Restart/Menu ---
  const handleStartGame = () => setGameStarted(true);

  const resetGame = useCallback((startGame: boolean) => {
    setBaseHealth(CONSTANTS.BASE_HEALTH);
    setPreviousBaseHealth(CONSTANTS.BASE_HEALTH);
    setResources(CONSTANTS.INITIAL_RESOURCES);
    setWave(1);
    setGameOver(false);
    setGameStarted(startGame);
    setPaused(false);
    setCodexOpen(false);
    setDiscoveredTroops(getBaseTroops());
    setDraggingTroop(null);
    setDragPosition(null);
    setDraggingTroopId(null);
    setDraggingTroopOriginalPos(null);
    setHoveredTroopId(null);
    setIsPanning(false);
    setLastPanPosition(null);
    setZoom(CONSTANTS.ZOOM_INITIAL);
    setPanX(gameWidth / 2 - (mapWidth / 2) * CONSTANTS.ZOOM_INITIAL);
    setPanY(gameHeight / 2 - (mapHeight / 2) * CONSTANTS.ZOOM_INITIAL);
    setParticleEffects([]);
    setScreenShakeActive(false);
    setScreenShakeOffset({ x: 0, y: 0 });
    if (screenShakeAnimationId.current) {
      cancelAnimationFrame(screenShakeAnimationId.current);
    }
    // Reset tutorial state
    setInTutorial(false);
    setTutorialStep(0);
    // Reset troop costs to initial values
    const initialCosts: { [key in TroopType]?: number } = {};
    for (const [key, troop] of Object.entries(CONSTANTS.TROOPS)) {
      if (troop.cost > 0) {
        initialCosts[key as TroopType] = troop.cost;
      }
    }
    setTroopCosts(initialCosts);
    troopId.current = 1;
    enemyId.current = 1;
    projectileId.current = 1;
    particleEffectId.current = 1;
    lastTimestamp.current = 0;
    waveCycleTimer.current = 0;
    spawnTimer.current = 0;
    setTroops([]);
    setEnemies([]);
    setProjectiles([]);
    setBackgroundTiles(generateBackgroundTiles());
    setMaxTroops(CONSTANTS.INITIAL_MAX_TROOPS);
  }, [gameWidth, gameHeight, mapWidth, mapHeight, generateBackgroundTiles]);

  const handleRestartGame = useCallback(() => {
    resetGame(true);
  }, [resetGame]);

  const handleReturnToMenu = useCallback(() => {
    resetGame(false);
  }, [resetGame]);

  // --- Pause/Resume Handlers ---
  const handlePause = () => setPaused(true);
  const handleResume = () => setPaused(false);
  const handleOpenCodex = () => {
    setCodexOpen(true);
    if (inTutorial && tutorialStep === 6) {
      setTutorialStep(7);
    }
  };
  const handleCloseCodex = () => {
    setCodexOpen(false);
    if (inTutorial && tutorialStep === 7) {
      setTutorialStep(8);
    }
  };

  // --- Settings Handlers ---
  const handleOpenSettings = () => setSettingsOpen(true);
  const handleCloseSettings = () => {
    menuClickSound.play();
    setSettingsOpen(false);
  };
  const handleMusicVolumeChange = (volume: number) => setMusicVolume(volume);
  const handleSfxVolumeChange = (volume: number) => {
    setSfxVolume(volume);
    if (!menuSelectSound.playing()) {
      menuSelectSound.play();
    }
  };

  // --- Credits Handlers ---
  const handleOpenCredits = () => setCreditsOpen(true);
  const handleCloseCredits = () => {
    menuClickSound.play();
    setCreditsOpen(false);
  };

  // --- Tutorial Handlers ---
  const handleStartTutorial = () => {
    resetGame(true);
    setInTutorial(true);
    setTutorialStep(0);
    setResources(CONSTANTS.TUTORIAL_INITIAL_RESOURCES);
    setMaxTroops(CONSTANTS.TUTORIAL_MAX_TROOPS);
  };

  const handleEndTutorial = () => {
    handleReturnToMenu();
  };

  const handleNextTutorialStep = () => {
    setTutorialStep(prev => prev + 1);
  };

  // --- Menu Click Handler ---
  const handleMenuClick = (itemId: string) => {
    menuClickSound.play();
    if (itemId === 'start') {
      handleStartGame();
    } else if (itemId === 'tutorial') {
      handleStartTutorial();
    } else if (itemId === 'settings') {
      handleOpenSettings();
    } else if (itemId === 'credits') {
      handleOpenCredits();
    } else {
      console.log(`Clicked on: ${itemId}`);
      // Placeholder for other menu actions
    }
  };

  // --- Render ---
  const currentTutorialStepData = inTutorial ? CONSTANTS.TUTORIAL_STEPS[tutorialStep] : null;
  const isLastTutorialStep = inTutorial && tutorialStep === CONSTANTS.TUTORIAL_STEPS.length - 1;

  if (isSplashVisible) {
    return <SplashScreen onFinished={() => setIsSplashVisible(false)} />;
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-900" style={{ width: "100vw", height: "100vh" }}>
      <div
        className="relative"
        style={{
          width: gameWidth,
          height: gameHeight,
          cursor: (draggingTroop || draggingTroopId !== null || isPanning) ? 'grabbing' : 'grab',
          transform: `translate(${screenShakeOffset.x}px, ${screenShakeOffset.y}px)`,
        }}
      >
        <div
          className="border-4 border-gray-700 rounded-lg shadow-lg"
          style={{
            width: gameWidth,
            height: gameHeight,
            background: gameStarted ? CONSTANTS.GAME_BACKGROUND_COLOR : "transparent",
            position: "relative",
          }}
        >
          <canvas
            ref={canvasRef}
            width={gameWidth}
            height={gameHeight}
            style={{ 
              display: "block", 
              width: "100%", 
              height: "100%", 
              cursor: (draggingTroop || draggingTroopId !== null || isPanning) ? "grabbing" : (hoveredTroopId !== null ? 'pointer' : 'grab') 
            }}
            onMouseDown={handleCanvasMouseDown}
          />
        </div>
        {gameStarted && !gameOver && (
          <>
            {/* Top UI */}
            <div className="absolute top-4 left-4 right-4 flex justify-between pointer-events-none" style={{ maxWidth: gameWidth - 32 }}>
              <div className="flex gap-4">
                <InfoPanel iconSrc={ASSETS.sprites.base_health_icon.path} value={baseHealth} total={CONSTANTS.BASE_HEALTH} barColor="bg-green-500" />
                <InfoPanel iconSrc={ASSETS.sprites.currency_coin.path} value={resources} />
                <div className="flex items-center bg-gray-900/80 rounded-lg shadow-lg text-white pointer-events-auto">
                    <div className="flex items-center p-2 min-w-[120px]">
                        <img src={ASSETS.sprites.troop_count_icon.path} alt="icon" className="w-7 h-7 mr-3" />
                        <div className="flex-grow">
                            <div className="font-bold text-lg">{`${troops.length} / ${maxTroops}`}</div>
                            <div className="w-full bg-gray-600 rounded-full h-2.5 mt-1">
                                <div className={`bg-purple-500 h-2.5 rounded-full`} style={{ width: `${(troops.length / maxTroops) * 100}%` }}></div>
                            </div>
                        </div>
                    </div>
                    <button 
                        onClick={handleUpgradeMaxTroops} 
                        disabled={resources < troopLimitUpgradeCost || paused || codexOpen || inTutorial} 
                        className="p-2 h-full bg-gray-800 hover:bg-green-600 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-r-lg border-l-2 border-gray-700 flex flex-col items-center justify-center transition-colors"
                        title={`Increase troop limit. Cost: ${troopLimitUpgradeCost}`}
                    >
                        <PlusCircle className="w-7 h-7" />
                        <div className="flex items-center text-xs text-yellow-400 mt-1">
                            <img src={ASSETS.sprites.currency_coin.path} alt="cost" className="w-3 h-3 mr-1" />
                            <span>{troopLimitUpgradeCost}</span>
                        </div>
                    </button>
                </div>
              </div>
              <div className="flex gap-4 items-center">
                <motion.div animate={waveAnimationControls}>
                    <InfoPanel iconSrc={ASSETS.sprites.wave_icon.path} value={`Wave ${wave}`} />
                </motion.div>
                {/* Codex Button */}
                <button
                  className="ml-4 pointer-events-auto bg-gray-800 hover:bg-yellow-400 hover:text-gray-900 text-white rounded-full p-3 shadow-lg border-2 border-gray-700 hover:border-yellow-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ outline: "none" }}
                  onClick={handleOpenCodex}
                  aria-label="Open Codex"
                  disabled={paused || codexOpen}
                >
                  <BookOpen className="w-8 h-8" />
                </button>
                {/* Pause Button */}
                <button
                  className="ml-4 pointer-events-auto bg-gray-800 hover:bg-yellow-400 hover:text-gray-900 text-white rounded-full p-3 shadow-lg border-2 border-gray-700 hover:border-yellow-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ outline: "none" }}
                  onClick={handlePause}
                  aria-label="Pause"
                  disabled={paused || codexOpen || inTutorial}
                >
                  <Pause className="w-8 h-8" />
                </button>
              </div>
            </div>
            {/* Bottom UI - Troop Selection */}
            <TroopSelector
              draggingTroop={draggingTroop}
              onTroopMouseDown={handleTroopMouseDown}
              resources={resources}
              paused={paused || codexOpen}
              currentTroopCount={troops.length}
              maxTroops={maxTroops}
              troopCosts={troopCosts}
              inTutorial={inTutorial}
              tutorialStep={tutorialStep}
            />
          </>
        )}
        {/* Main Menu */}
        {!gameStarted && <MainMenu onMenuClick={handleMenuClick} />}
        
        {/* Game Over Modal */}
        {gameOver && <GameOverModal wave={wave} highScore={highScoreWave} onRestart={handleRestartGame} onReturnToMenu={handleReturnToMenu} />}

        {/* Pause Modal */}
        {paused && !codexOpen && gameStarted && !gameOver && <PauseModal onResume={handleResume} onReturnToMenu={handleReturnToMenu} />}

        {/* Codex Modal */}
        {codexOpen && <CodexModal onClose={handleCloseCodex} discoveredTroops={discoveredTroops} />}

        {/* Settings Modal */}
        {settingsOpen && (
          <SettingsModal
            onClose={handleCloseSettings}
            musicVolume={musicVolume}
            sfxVolume={sfxVolume}
            onMusicVolumeChange={handleMusicVolumeChange}
            onSfxVolumeChange={handleSfxVolumeChange}
          />
        )}

        {/* Credits Modal */}
        {creditsOpen && <CreditsModal onClose={handleCloseCredits} />}

        {/* Tutorial Guide */}
        {inTutorial && gameStarted && !gameOver && currentTutorialStepData && (
          <TutorialGuide
            step={currentTutorialStepData}
            onNext={currentTutorialStepData.hasButton && !isLastTutorialStep ? handleNextTutorialStep : undefined}
            onEnd={isLastTutorialStep ? handleEndTutorial : undefined}
          />
        )}

        {/* Particle Effects */}
        {particleEffects.map(effect => (
          <ParticleEffect
            key={effect.id}
            isActive={effect.isActive}
            centerX={effect.x}
            centerY={effect.y}
            onComplete={() => handleParticleEffectComplete(effect.id)}
          />
        ))}
      </div>
    </div>
  );
}

export default App;
